import { supabase } from '@/lib/supabase';

const BUCKET = 'email-images';
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_MIME = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

const EXT_BY_MIME: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

export interface ImageUploadResult {
  url: string;
  path: string;
}

export class ImageUploadError extends Error {
  constructor(
    public readonly code:
      | 'invalid_type'
      | 'too_large'
      | 'no_workspace'
      | 'upload_failed',
    message: string,
  ) {
    super(message);
    this.name = 'ImageUploadError';
  }
}

/**
 * Upload an image to the `email-images` bucket under the caller's workspace
 * folder. Returns the public URL suitable for embedding in an `<img src>`.
 *
 * Used by the email template editor and the campaign wizard composer. The
 * one-off composer (EmailComposer.tsx) intentionally does NOT use this —
 * it relies on base64 + send-time MIME CID conversion since one-off emails
 * are ephemeral (no DB persistence beyond the audit row).
 */
export async function uploadEmailImage(
  file: File,
  workspaceId: string,
): Promise<ImageUploadResult> {
  if (!workspaceId) {
    throw new ImageUploadError('no_workspace', 'No workspace selected');
  }
  if (!ALLOWED_MIME.has(file.type)) {
    // Email body content can only embed images. Non-image files (PDFs,
    // JSON, etc.) would need to be sent as attachments — and the Hermes
    // send path does not currently support attachments at all, so framing
    // the message to make the constraint explicit avoids the "but I tried
    // a .json" confusion.
    throw new ImageUploadError(
      'invalid_type',
      `Only images can be inserted (PNG, JPEG, GIF, WebP). ` +
        `"${file.type || file.name}" isn't a supported image format. ` +
        `Email attachments for other file types aren't currently supported — ` +
        `host the file elsewhere and add a link instead.`,
    );
  }
  if (file.size > MAX_BYTES) {
    throw new ImageUploadError(
      'too_large',
      `Image is ${Math.round(file.size / 1024)} KB; max is ${MAX_BYTES / 1024 / 1024} MB. ` +
        `Compress the image (try TinyPNG or Squoosh) and try again.`,
    );
  }

  const ext = EXT_BY_MIME[file.type];
  const uuid = crypto.randomUUID();
  const path = `workspaces/${workspaceId}/${uuid}.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type,
    });

  if (error) {
    throw new ImageUploadError('upload_failed', error.message);
  }

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  if (!data?.publicUrl) {
    throw new ImageUploadError('upload_failed', 'Could not resolve public URL');
  }

  return { url: data.publicUrl, path };
}
